import { type ColumnDef } from '@tanstack/react-table';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { type GalleryView } from '@/types';

interface StatusesColumnOptions<T> {
  id?: string;
  accessorFn?: (row: T) => string;
  accessorKey: keyof T;
  enableSorting?: boolean;
  enableHiding?: boolean;
  minSize?: number;
  maxSize?: number;
  label: string;
  size?: number;
  /**
   * In the fixed-layout table this column takes no fixed width and absorbs the
   * leftover space, so the status chips get room to grow instead of overflowing
   * a locked-width cell. (DM-2393)
   */
  flexWidth?: boolean;
  cellClassName?: string;
  headerClassName?: string;
  galleryView?: GalleryView;
  dataCy?: string;
}

export const StatusesColumn = <T extends Record<string, any>>(opts: StatusesColumnOptions<T>): ColumnDef<T> => {
  const {
    id,
    accessorFn,
    accessorKey,
    // A modest floor for the flexed column; it grows past this to share leftover space.
    size = 100,
    label,
    cellClassName,
    headerClassName,
    enableSorting = false,
    enableHiding = true,
    minSize = 100,
    maxSize,
    flexWidth = false,
    galleryView,
    dataCy,
  } = opts;

  return {
    id,
    accessorFn,
    accessorKey,
    enableSorting,
    enableHiding,
    minSize,
    maxSize,
    size,
    meta: {
      flexWidth,
      ...(cellClassName && { cellClassName }),
      ...(headerClassName && { headerClassName }),
    },
    header: () => label,
    cell: ({ getValue, row }) => {
      const raw = getValue() as Record<string, { id: string; name: string }>;

      return (
        <div
          className="flex items-center gap-2 whitespace-nowrap"
          data-cy={dataCy ?? `${galleryView ?? 'list'}-${row.index}-${id}`}
        >
          {Object.values(raw).map(({ id: statusId, name }) => (
            <Tooltip key={statusId}>
              <TooltipTrigger asChild>
                <Badge className="max-w-[20ch]" variant="light">
                  <span className="min-w-0 truncate">{name}</span>
                </Badge>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent className="max-w-44 break-all">{name}</TooltipContent>
              </TooltipPortal>
            </Tooltip>
          ))}
        </div>
      );
    },
  };
};
